import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, FormControlLabel, IconButton, MenuItem, Select, Slider, Stack, Switch, Typography } from '@mui/material';
import { Pause, PlayArrow, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { DiagnosticAudioClock, diagnosticDecodedBytes, diagnosticPeaks } from './diagnosticAudioClock';

interface Track {
  role: 'contact_rx' | 'contact_tx';
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'expired';
  fileSize: number;
  sampleRate: number;
  durationSeconds: number;
  mixOffsetMs: number;
  downloadUrl: string | null;
}
interface RecordingDiagnostics { captureEnabled: boolean; tracks: Track[] }
const MAX_DECODE_BYTES = 64 * 1024 * 1024;
const MAX_PCM_BYTES = 128 * 1024 * 1024;

/** Internal RX/TX viewer. The parent must gate mounting with the raw user.isSuperuser. */
export default function DiagnosticRecordingTracksViewer({ callId }: { callId: string }) {
  const { t } = useTranslation('pages');
  const [data, setData] = useState<RecordingDiagnostics | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState<{ track: Track; peaks: number[] }[]>([]);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState<boolean[]>([]);
  const clock = useRef<DiagnosticAudioClock | null>(null);
  const loadController = useRef<AbortController | null>(null);
  const seeking = useRef(false);
  const refresh = async () => {
    loadController.current?.abort();
    await clock.current?.close(); clock.current = null;
    setLoaded([]); setPosition(0); setPlaying(false);
    setLoading(false);
    try {
      const response = await api.get<RecordingDiagnostics>(`/api/calls/${callId}/recording-diagnostics/`);
      setData(response.data); setError(false);
    } catch { setError(true); }
  };
  useEffect(() => {
    const controller = new AbortController();
    let polls = 0;
    const fetchState = async () => {
      try {
        const response = await api.get<RecordingDiagnostics>(`/api/calls/${callId}/recording-diagnostics/`, { signal: controller.signal });
        if (!controller.signal.aborted) setData(response.data);
      } catch { if (!controller.signal.aborted) setError(true); }
    };
    void fetchState();
    const interval = window.setInterval(() => { if (++polls <= 20) void fetchState(); }, 15000);
    return () => { controller.abort(); window.clearInterval(interval); loadController.current?.abort(); void clock.current?.close(); };
  }, [callId]);
  useEffect(() => {
    if (!playing) return;
    let frame: number;
    const tick = () => {
      if (!clock.current) return;
      const current = Math.max(0, clock.current.position);
      if (!seeking.current) setPosition(current);
      if (current >= clock.current.duration) { clock.current.pause(); setPlaying(false); return; }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  /** Fetch and decode on demand, allowing a partial capture to remain useful. */
  const load = async () => {
    const tracks = data?.tracks.filter(track => track.downloadUrl) ?? [];
    if (tracks.reduce((sum, track) => sum + track.fileSize, 0) > MAX_DECODE_BYTES) { setError(true); return; }
    setLoading(true); setError(false);
    loadController.current?.abort();
    const controller = new AbortController(); loadController.current = controller;
    await clock.current?.close(); clock.current = null;
    setLoaded([]); setPosition(0); setPlaying(false); setRate(1);
    let context: AudioContext | null = null;
    try {
    context = new AudioContext();
    let rawBytes = 0; let decodedBytes = 0;
    const activeContext = context;
    const results = await Promise.allSettled(tracks.map(async track => {
      const response = await fetch(track.downloadUrl!, { signal: controller.signal });
      if (!response.ok) throw new Error('diagnostic_audio_unavailable');
      const buffer = await response.arrayBuffer();
      rawBytes += buffer.byteLength;
      decodedBytes += diagnosticDecodedBytes(buffer, activeContext.sampleRate);
      if (rawBytes > MAX_DECODE_BYTES || decodedBytes > MAX_PCM_BYTES) throw new Error('diagnostic_audio_too_large');
      return { track, buffer: await activeContext.decodeAudioData(buffer) };
    }));
    if (controller.signal.aborted) { await context.close(); return; }
    const decoded = results.flatMap(result => result.status === 'fulfilled' ? [result.value] : []);
    if (!decoded.length) { await context.close(); setError(true); }
    else {
      clock.current = new DiagnosticAudioClock(context, decoded.map(item => item.buffer));
      setLoaded(decoded.map(item => ({ track: item.track, peaks: diagnosticPeaks(item.buffer) })));
      setMuted(decoded.map(() => false));
      setError(decoded.length < tracks.length);
    }
    } catch {
      if (!controller.signal.aborted) setError(true);
      if (context && context.state !== 'closed') await context.close();
    } finally { if (!controller.signal.aborted) setLoading(false); }
  };
  if (!data?.captureEnabled && !data?.tracks.length && !error) return null;
  const duration = clock.current?.duration ?? 0;
  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontWeight={600}>{t('calls.diagnosticTracks.title')}</Typography>
          <IconButton aria-label={t('calls.diagnosticTracks.refresh')} onClick={() => void refresh()}><Refresh /></IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary">{t('calls.diagnosticTracks.description')}</Typography>
        {error && <Alert severity="warning">{t('calls.diagnosticTracks.error')}</Alert>}
        {!data?.tracks.length && <Typography variant="body2">{t('calls.diagnosticTracks.unavailable')}</Typography>}
        {data?.tracks.map(track => (
          <Stack key={track.role} direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">{t(`calls.diagnosticTracks.${track.role}`)}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={t(`calls.diagnosticTracks.status.${track.status}`)} />
              {track.downloadUrl && <Button size="small" component="a" href={track.downloadUrl} target="_blank" rel="noreferrer">{t('calls.diagnosticTracks.download')}</Button>}
            </Stack>
          </Stack>
        ))}
        {!loaded.length && data?.tracks.some(track => track.downloadUrl) && (
          <Button disabled={loading} onClick={() => void load()}>{t(loading ? 'calls.diagnosticTracks.loading' : 'calls.diagnosticTracks.load')}</Button>
        )}
        {loaded.map((item, index) => (
          <Box key={item.track.role}>
            <FormControlLabel control={<Switch checked={muted[index] ?? false} onChange={(_, checked) => {
              clock.current?.mute(index, checked); setMuted(previous => previous.map((value, i) => i === index ? checked : value));
            }} />} label={`${t(`calls.diagnosticTracks.${item.track.role}`)} — ${t('calls.diagnosticTracks.mute')}`} />
            <Box component="svg" role="img" aria-label={t(`calls.diagnosticTracks.${item.track.role}`)} viewBox="0 0 200 40" preserveAspectRatio="none" sx={{ display: 'block', width: '100%', height: 52, color: index === 0 ? 'info.main' : 'primary.main' }}>
              {item.peaks.map((peak, bar) => <line key={bar} x1={bar * item.track.durationSeconds / duration} x2={bar * item.track.durationSeconds / duration} y1={20 - peak * 19} y2={20 + peak * 19} stroke="currentColor" strokeWidth="0.7" opacity={muted[index] ? 0.3 : 1} />)}
              <line x1={duration ? position / duration * 200 : 0} x2={duration ? position / duration * 200 : 0} y1="0" y2="40" stroke="currentColor" strokeWidth="1" />
            </Box>
          </Box>
        ))}
        {loaded.length > 0 && <Stack direction="row" spacing={2} alignItems="center">
          <IconButton aria-label={t(playing ? 'calls.diagnosticTracks.pause' : 'calls.diagnosticTracks.play')} onClick={async () => {
            try { if (playing) clock.current?.pause(); else await clock.current?.play(); setPlaying(!playing); } catch { setError(true); }
          }}>{playing ? <Pause /> : <PlayArrow />}</IconButton>
          <Slider aria-label={t('calls.diagnosticTracks.seek')} value={position} min={0} max={Math.max(duration, 0.01)} step={0.01} onChange={(_, value) => { seeking.current = true; setPosition(value as number); }} onChangeCommitted={async (_, value) => { await clock.current?.seek(value as number); seeking.current = false; }} />
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>{Math.floor(position / 60)}:{String(Math.floor(position % 60)).padStart(2, '0')}</Typography>
          <Select size="small" value={rate} inputProps={{ 'aria-label': t('calls.diagnosticTracks.speed') }} onChange={event => { const next = Number(event.target.value); setRate(next); void clock.current?.setRate(next); }}>
            {[0.5, 0.75, 1, 1.25, 1.5, 2].map(value => <MenuItem key={value} value={value}>{value}×</MenuItem>)}
          </Select>
        </Stack>}
      </Stack>
    </Box>
  );
}
